import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import {
  SystemDropdownCategory,
  SystemDropdownOptionResponse,
  SystemDropdownParameter,
} from "../client-file/api/ClientFileApi";
import { useLookupsApi } from "../client-file/api/ClientFileApiContext";
import { ClientFileQueryKey } from "../client-file/query/clientFileQueryKeys";
import { IOptionItem } from "../formik/formik-autocomplete/FormikAutocomplete.types";

const TWO_HOURS = 2 * 60 * 60 * 1000;

export const systemDropdownOptionsQueryKey = (category: SystemDropdownCategory) => [
  ClientFileQueryKey.SystemDropdownOptions,
  category,
];

export interface SystemDropdownOption {
  /** What is stored and what application logic references. Never shown to the user. */
  key: string;
  /** The label shown in the dropdown. */
  label: string;
}

export interface SystemDropdownOptions {
  /** The selectable values. Hidden entries are left out. */
  options: SystemDropdownOption[];
  /**
   * The values in the shape the autocomplete fields expect, memoized so they stay referentially
   * stable. Hidden entries are included and carry {@link IOptionItem#isHidden}: the fields keep them
   * out of the list but resolve a stored key through them, so a record holding a deactivated value
   * still shows its label.
   */
  optionItems: IOptionItem[];
  /** Resolves a stored key to its label, falling back to the key so a value is never rendered blank. */
  labelOf: (key: string | null | undefined) => string;
  parameterOf: (key: string | null | undefined, parameter: SystemDropdownParameter) => string | undefined;
  /**
   * A boolean parameter's value, mirroring the backend's `GetBooleanParameter`: anything but the
   * stored `"true"` reads as false, so an unknown key or an unset parameter carries no behaviour.
   */
  booleanParameterOf: (key: string | null | undefined, parameter: SystemDropdownParameter) => boolean;
  isLoading: boolean;
}

/** The encoding of a true boolean parameter, as the backend persists it. */
const BooleanParameterTrue = "true";

/**
 * Reads one system-relevant dropdown's value list. Hidden entries are fetched too, so a deactivated
 * value stops being offered without records that reference one falling back to showing its raw key.
 */
export function useSystemDropdownOptions(category: SystemDropdownCategory): SystemDropdownOptions {
  const lookupsApi = useLookupsApi();

  const { data, isLoading } = useQuery<SystemDropdownOptionResponse[]>({
    queryKey: systemDropdownOptionsQueryKey(category),
    queryFn: () => lookupsApi.getSystemDropdownOptions({ category, includeHidden: true }),
    staleTime: TWO_HOURS,
  });

  return useMemo(() => {
    const entries = data ?? [];
    const byKey = new Map(entries.map((entry) => [entry.key, entry]));

    const options = entries.filter((entry) => !entry.isHidden).map((entry) => ({ key: entry.key, label: entry.value }));

    const parameterOf = (key: string | null | undefined, parameter: SystemDropdownParameter) =>
      key ? byKey.get(key)?.parameters.find((p) => p.parameter === parameter)?.value : undefined;

    return {
      options,
      optionItems: entries.map((entry) => ({ id: entry.key, label: entry.value, isHidden: entry.isHidden })),
      labelOf: (key) => (key ? (byKey.get(key)?.value ?? key) : ""),
      parameterOf,
      booleanParameterOf: (key, parameter) => parameterOf(key, parameter) === BooleanParameterTrue,
      isLoading,
    };
  }, [data, isLoading]);
}
